/**
 * Mapa de rotas (TanStack Router, code-based e tipado). Páginas carregadas sob
 * demanda (lazyRouteComponent) para manter o bundle inicial pequeno.
 *
 *  públicas: /login /signup /verify-email /forgot-password /reset-password /auth/callback
 *  logado:   /onboarding  /projects/$p/domains/$d/tailorkit (tela cheia)
 *  painel:   /  /projects  /projects/$p  /projects/$p/domains  /projects/$p/domains/$d
 *            /projects/$p/domains/$d/analytics (estado do dashboard na URL)  /projects/$p/domains/$d/seo?tab=
 *            /projects/$p/customization  /billing  /api-keys  /account  /organization
 */
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  type ParsedLocation,
} from "@tanstack/react-router";
import { NotFoundPage, RootErrorPage } from "./components/layout/RootPages";
import { Spinner } from "./components/ui/misc";
import { Toaster } from "./components/ui/toast";
import { validateAnalyticsSearch } from "./features/analytics/search";
import { AuthLayout } from "./features/auth/AuthLayout";
import { HelpDrawerHost } from "./features/help/HelpDrawerHost";
import type { Me } from "./lib/api-types";
import { meQuery, sessionQuery } from "./lib/query";

export type RouterContext = { queryClient: QueryClient };

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
/** ?add=1 chega como número (o router faz JSON.parse da query). */
const flag = (v: unknown) => (v === true || v === 1 || v === "1" || v === "true" ? true : undefined);

// ── raiz ───────────────────────────────────────────────────
export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <Outlet />
      <Toaster />
      <HelpDrawerHost />
    </>
  ),
  notFoundComponent: NotFoundPage,
  errorComponent: RootErrorPage,
});

// ── públicas (auth) ────────────────────────────────────────
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth",
  component: AuthLayout,
});

/** Já logado? Vai direto para o painel. */
async function redirectIfLoggedIn(queryClient: QueryClient) {
  const session = await queryClient.ensureQueryData(sessionQuery).catch(() => null);
  if (session) throw redirect({ to: "/" });
}

export const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "login",
  validateSearch: (s: Record<string, unknown>): { redirect?: string | undefined; error?: string | undefined } => ({
    redirect: str(s.redirect),
    error: str(s.error),
  }),
  beforeLoad: ({ context }) => redirectIfLoggedIn(context.queryClient),
  component: lazyRouteComponent(() => import("./features/auth/LoginPage"), "LoginPage"),
});

export const signupRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "signup",
  beforeLoad: ({ context }) => redirectIfLoggedIn(context.queryClient),
  component: lazyRouteComponent(() => import("./features/auth/SignupPage"), "SignupPage"),
});

export const verifyEmailRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "verify-email",
  validateSearch: (s: Record<string, unknown>): { email?: string | undefined } => ({ email: str(s.email) }),
  component: lazyRouteComponent(() => import("./features/auth/VerifyEmailPage"), "VerifyEmailPage"),
});

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "forgot-password",
  component: lazyRouteComponent(() => import("./features/auth/ForgotPasswordPage"), "ForgotPasswordPage"),
});

export const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "reset-password",
  validateSearch: (s: Record<string, unknown>): { token?: string | undefined; error?: string | undefined } => ({
    token: str(s.token),
    error: str(s.error),
  }),
  component: lazyRouteComponent(() => import("./features/auth/ResetPasswordPage"), "ResetPasswordPage"),
});

/** Destino do magic link e da confirmação de e-mail (o better-auth redireciona para cá). */
export const authCallbackRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "auth/callback",
  validateSearch: (s: Record<string, unknown>): { error?: string | undefined; redirect?: string | undefined } => ({
    error: str(s.error),
    redirect: str(s.redirect),
  }),
  component: lazyRouteComponent(() => import("./features/auth/AuthCallbackPage"), "AuthCallbackPage"),
});

/** Convite para uma organização: prévia pública; aceitar exige login com o e-mail convidado. */
export const inviteRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "invite/$token",
  component: lazyRouteComponent(() => import("./features/auth/InvitePage"), "InvitePage"),
});

/** Portal do cliente final do SaaS: público, autenticado só pelo link (#token), com a marca do SaaS. */
export const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "portal",
  component: lazyRouteComponent(() => import("./features/portal/PortalPage"), "PortalPage"),
});

// ── logado ─────────────────────────────────────────────────

/** Rotas acessíveis mesmo sem assinatura ativa. */
const ALLOWED_WHEN_INACTIVE = ["/billing", "/onboarding", "/account", "/organization"];

/** Primeiro acesso: ainda sem perfil e sem assinatura → wizard. */
export const needsOnboarding = (me: Me) => !me.active && !me.subscription && !me.organization.profile;

export function gateRedirect(me: Me, pathname: string): "/onboarding" | "/billing" | null {
  if (me.active) return null;
  // A administração da plataforma independe da assinatura da própria organização (é por ali que se dá cortesia).
  if (me.platformAdmin && (pathname === "/admin" || pathname.startsWith("/admin/"))) return null;
  if (ALLOWED_WHEN_INACTIVE.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null;
  // Como no Bubble: sem assinatura ativa, o painel força a tela de planos.
  return needsOnboarding(me) ? "/onboarding" : "/billing";
}

async function requireAuth(queryClient: QueryClient, location: ParsedLocation) {
  const session = await queryClient.ensureQueryData(sessionQuery);
  if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });
  const me = await queryClient.ensureQueryData(meQuery);
  const target = gateRedirect(me, location.pathname);
  if (target) throw redirect({ to: target });
  return { session, me };
}

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  beforeLoad: ({ context, location }) => requireAuth(context.queryClient, location),
  pendingComponent: FullPageSpinner,
});

export const onboardingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "onboarding",
  component: lazyRouteComponent(() => import("./features/onboarding/OnboardingPage"), "OnboardingPage"),
});

export const tailorkitRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "projects/$projectId/domains/$domainId/tailorkit",
  component: lazyRouteComponent(() => import("./features/tailorkit/TailorkitPage"), "TailorkitPage"),
});

const shellRoute = createRoute({
  getParentRoute: () => protectedRoute,
  id: "shell",
  component: lazyRouteComponent(() => import("./components/layout/AppShell"), "AppShell"),
});

export const homeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./features/home/HomePage"), "HomePage"),
});

export const projectsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "projects",
  validateSearch: (s: Record<string, unknown>): { new?: boolean | undefined } => ({ new: flag(s.new) }),
  component: lazyRouteComponent(() => import("./features/projects/ProjectsPage"), "ProjectsPage"),
});

export const projectRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "projects/$projectId",
  component: lazyRouteComponent(() => import("./features/projects/ProjectLayout"), "ProjectLayout"),
});

export const projectOverviewRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./features/projects/ProjectOverviewPage"), "ProjectOverviewPage"),
});

export const projectDomainsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "domains",
  validateSearch: (s: Record<string, unknown>): { add?: boolean | undefined } => ({ add: flag(s.add) }),
  component: lazyRouteComponent(() => import("./features/domains/ProjectDomainsPage"), "ProjectDomainsPage"),
});

export const domainDetailRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "domains/$domainId",
  /** ?tab= abre uma aba (links "Abrir no painel" da ajuda). */
  validateSearch: (s: Record<string, unknown>): { tab?: string | undefined } => ({ tab: str(s.tab) }),
  component: lazyRouteComponent(() => import("./features/domains/DomainDetailPage"), "DomainDetailPage"),
});

/** Analytics do domínio: período, comparação, filtros e agrupamento ficam na URL (compartilhável). */
export const domainAnalyticsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "domains/$domainId/analytics",
  validateSearch: validateAnalyticsSearch,
  component: lazyRouteComponent(() => import("./features/analytics/AnalyticsPage"), "AnalyticsPage"),
});

/** SEO avançado do domínio (?tab=… escolhe a aba; ?gsc=… é o retorno do OAuth do Google). */
export const domainSeoRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "domains/$domainId/seo",
  validateSearch: (s: Record<string, unknown>): { tab?: string | undefined; gsc?: string | undefined } => ({
    tab: str(s.tab),
    gsc: str(s.gsc),
  }),
  component: lazyRouteComponent(() => import("./features/seo/SeoPage"), "SeoPage"),
});

/** Testes A/B do domínio (landing pages no mesmo endereço + testes do TailorKit); ?test=<id> abre o painel do teste. */
export const domainExperimentsRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "domains/$domainId/experiments",
  validateSearch: (s: Record<string, unknown>): { test?: string | undefined } => ({ test: str(s.test) }),
  component: lazyRouteComponent(() => import("./features/experiments/ExperimentsPage"), "ExperimentsPage"),
});

export const projectCustomizationRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "customization",
  component: lazyRouteComponent(() => import("./features/rules/ProjectCustomizationPage"), "ProjectCustomizationPage"),
});

export const projectStatusRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "status",
  component: lazyRouteComponent(() => import("./features/status/StatusPage"), "StatusPageScreen"),
});

export const projectPortalRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "portal",
  component: lazyRouteComponent(() => import("./features/portal/PortalSettingsPage"), "PortalSettingsPage"),
});

export const projectIntegrationRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "integration",
  component: lazyRouteComponent(() => import("./features/integrations/IntegrationPage"), "IntegrationPage"),
});

export const projectApisRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: "apis",
  component: lazyRouteComponent(() => import("./features/scale/ScalePage"), "ScalePage"),
});

export const billingRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "billing",
  validateSearch: (s: Record<string, unknown>): { checkout?: "success" | "canceled" | undefined } => ({
    checkout: s.checkout === "success" || s.checkout === "canceled" ? s.checkout : undefined,
  }),
  component: lazyRouteComponent(() => import("./features/billing/BillingPage"), "BillingPage"),
});

export const apiKeysRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "api-keys",
  component: lazyRouteComponent(() => import("./features/api-keys/ApiKeysPage"), "ApiKeysPage"),
});

export const adminRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "admin",
  component: lazyRouteComponent(() => import("./features/admin/AdminPage"), "AdminPage"),
});

export const accountRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "account",
  component: lazyRouteComponent(() => import("./features/account/AccountPage"), "AccountPage"),
});

export const organizationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "organization",
  validateSearch: (s: Record<string, unknown>): { tab?: string | undefined } => ({ tab: str(s.tab) }),
  component: lazyRouteComponent(() => import("./features/account/OrganizationPage"), "OrganizationPage"),
});

// ── central de ajuda (os mesmos artigos de docs.tenanci.com) ──
const helpLayoutRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "help",
  component: lazyRouteComponent(() => import("./features/help/HelpPages"), "HelpLayout"),
});
export const helpIndexRoute = createRoute({
  getParentRoute: () => helpLayoutRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./features/help/HelpPages"), "HelpHomePage"),
});
export const helpArticleRoute = createRoute({
  getParentRoute: () => helpLayoutRoute,
  path: "$slug",
  component: lazyRouteComponent(() => import("./features/help/HelpPages"), "HelpArticlePage"),
});
export const helpApiRoute = createRoute({
  getParentRoute: () => helpLayoutRoute,
  path: "api",
  component: lazyRouteComponent(() => import("./features/help/ApiRefPages"), "ApiIndexPage"),
});
export const helpApiGroupRoute = createRoute({
  getParentRoute: () => helpLayoutRoute,
  path: "api/$group",
  component: lazyRouteComponent(() => import("./features/help/ApiRefPages"), "ApiGroupPage"),
});

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([loginRoute, signupRoute, verifyEmailRoute, forgotPasswordRoute, resetPasswordRoute, authCallbackRoute, inviteRoute]),
  portalRoute,
  protectedRoute.addChildren([
    onboardingRoute,
    tailorkitRoute,
    shellRoute.addChildren([
      homeRoute,
      projectsRoute,
      projectRoute.addChildren([projectOverviewRoute, projectDomainsRoute, domainDetailRoute, domainAnalyticsRoute, domainSeoRoute, domainExperimentsRoute, projectCustomizationRoute, projectStatusRoute, projectPortalRoute, projectIntegrationRoute, projectApisRoute]),
      billingRoute,
      adminRoute,
      apiKeysRoute,
      accountRoute,
      organizationRoute,
      helpLayoutRoute.addChildren([helpIndexRoute, helpApiRoute, helpApiGroupRoute, helpArticleRoute]),
    ]),
  ]),
]);

function FullPageSpinner() {
  return (
    <div className="grid h-full place-items-center p-10">
      <Spinner />
    </div>
  );
}

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    defaultPendingComponent: FullPageSpinner,
    defaultPendingMs: 250,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
